import { classToPlain, Exclude, Expose, plainToClass, Transform } from "class-transformer";
import * as t from "io-ts";
import "reflect-metadata";
import { v4 as uuid } from "uuid";
import { validateJSON } from "../web/validation";
import {
  ContainerAlertCargoSubtypeOptionsArrayValidator,
  ContainerAlertCargoSubtypeOptionsEntity
} from "./container-alert-cargo-subtype-options";
import { transformCargoSubtypeOptionsArray } from "./transfomers/transformers";

/** Validator for a container level type. */
export const ContainerAlertOptionsValidator = t.intersection([
  t.type({
    id: t.string,
    regionId: t.string,
    objectId: t.string,
    objectType: t.string,
    silenceMaxInterval: t.number,
    silenceMaxIntervalUom: t.string,
    reportType: t.string,
  }),
  t.partial({
    cargoSubtypeOptions: ContainerAlertCargoSubtypeOptionsArrayValidator,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
]);

export const ContainerAlertOptionsArrayValidator = t.array(
  ContainerAlertOptionsValidator
);

export type ContainerAlertOptions = t.TypeOf<
  typeof ContainerAlertOptionsValidator
>;
export type ContainerAlertOptionsArray = t.TypeOf<
  typeof ContainerAlertOptionsArrayValidator
>;

/**
 * A container alert options.
 */
@Exclude()
export class ContainerAlertOptionsEntity {
  @Expose() id!: string;
  @Expose() regionId!: string;
  @Expose() objectId!: string;
  @Expose() objectType!: string;
  @Expose() silenceMaxInterval = 0;
  @Expose() silenceMaxIntervalUom = 'hours';
  @Expose() reportType = 'None';

  @Transform(transformCargoSubtypeOptionsArray)
  @Expose({ name: 'cargoSubtypeOptions' })
  @Exclude({ toPlainOnly: true })
  cargoSubtypeOptions: ContainerAlertCargoSubtypeOptionsEntity[] = [];

  @Expose() private createDttm?: number;
  @Expose() private lastModifiedDttm?: number;

  /**
   * Constructor for ContainerAlertOptionsEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a ContainerAlertOptionsEntity.
   *
   * @param regionId the primary key of the region
   * @param objectId the primary key of the object
   * @param id (optional) The primary key of the container.
   */
  static create(
    regionId: string,
    objectId: string,
    id: string | undefined = uuid()
  ): ContainerAlertOptionsEntity {
    const entity = new ContainerAlertOptionsEntity();
    entity.id = id;
    entity.regionId = regionId;
    entity.objectId = objectId;
    return entity;
  }

  /**
   * Create a ContainerAlertOptionsEntity from a ContainerAlertOptions type value.
   *
   * @param containerAlertOptions The container alert options type value.
   */
  static from(
    containerAlertOptions: ContainerAlertOptions
  ): ContainerAlertOptionsEntity {
    const entity: ContainerAlertOptionsEntity = plainToClass(
      ContainerAlertOptionsEntity,
      containerAlertOptions,
      {
        excludeExtraneousValues: true,
      }
    );
    return entity;
  }

  /**
   * Deserializes the ContainerAlertOptionsEntity to a ContainerAlertOptions type value.
   * If validation fails, the promise will be rejected.
   */
  toTypeValue(): Promise<ContainerAlertOptions> {
    const object = classToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(ContainerAlertOptionsValidator, object);
  }

  get createDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.createDttm) {
      return new Date(this.createDttm);
    } else {
      return undefined;
    }
  }

  set createDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.createDttm = date.getTime();
    } else {
      this.createDttm = undefined;
    }
  }

  get lastModifiedDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.lastModifiedDttm) {
      return new Date(this.lastModifiedDttm);
    } else {
      return undefined;
    }
  }

  set lastModifiedDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.lastModifiedDttm = date.getTime();
    } else {
      this.lastModifiedDttm = undefined;
    }
  }
}
